import {
  deriveRequirementSet,
  renderedRiskSectionsInPdfSection,
  resolveRiskInputScopes,
} from "@repo/shared";
import type { PdfSectionScope, RiskModelScopes } from "@repo/shared/types";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SearchInput } from "@/components/ui/search-input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useCreatePdfTemplateFromTemplate } from "@/lib/hooks/use-pdf-templates";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import { cn } from "@/lib/utils";
import { T } from "./pdf-template-from-template-dialog.i18n";
import {
  PDF_TEMPLATE_CATEGORIES,
  PDF_TEMPLATES,
  type PdfTemplate,
  type PdfTemplateCategory,
} from "./templates";

// Get localized language name using Intl.DisplayNames
function getLocalizedLanguageName(langCode: string, userLanguage: string): string {
  try {
    const displayLocale = userLanguage.replace("_", "-");
    const targetLocale = langCode.replace("_", "-");
    const displayNames = new Intl.DisplayNames([displayLocale], { type: "language" });
    return displayNames.of(targetLocale) || langCode;
  } catch {
    return langCode;
  }
}

type Flow = {
  id: string;
  name: string;
  language?: string;
  // The requirement settings of the flow's assigned portfolio collection, or null
  // when the flow has no collection. Drives which flows a template can attach to.
  collectionSettings?: {
    requiresAssetAllocation: boolean;
    requiresTimeseries: boolean;
  } | null;
  // QL-212 — the three input scopes of the flow's risk model, or null when the
  // flow has none. Decides whether the template's risk section prints anything.
  riskScopes?: RiskModelScopes | null;
};

type PdfTemplateFromTemplateDialogProps = {
  open: boolean;
  onClose: () => void;
  flows: Flow[];
  supportedLanguages: string[];
  defaultLanguage: string;
};

export function PdfTemplateFromTemplateDialog({
  open,
  onClose,
  flows,
  supportedLanguages,
  defaultLanguage,
}: PdfTemplateFromTemplateDialogProps) {
  const navigate = useNavigate();
  const { userLanguage } = useUserLanguage();
  const t = useT(T);
  const [step, setStep] = useState<"select" | "configure">("select");
  const [selectedTemplate, setSelectedTemplate] = useState<PdfTemplate | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(["basic"]));
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [language, setLanguage] = useState(
    supportedLanguages.includes(userLanguage) ? userLanguage : defaultLanguage,
  );
  const [flowId, setFlowId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const createFromTemplateMutation = useCreatePdfTemplateFromTemplate();

  // Filter flows based on selected language
  const filteredFlows = useMemo(() => {
    return flows.filter((flow) => flow.language === language);
  }, [flows, language]);

  // Requirements derived from the template's actual components (single source of
  // truth in @repo/shared). Only the two collection-setting flags matter here —
  // they gate which flows (via their portfolio collection) can host the template.
  const requiredFlags = useMemo(() => {
    const componentTypes: string[] = [];
    for (const section of selectedTemplate?.sections ?? []) {
      for (const page of section.pages) {
        for (const comp of page.components) componentTypes.push(comp.type);
      }
    }
    return deriveRequirementSet(componentTypes).flags;
  }, [selectedTemplate]);

  // Setting keys the template requires but the flow's collection doesn't satisfy.
  // A flow with no collection (collectionSettings == null) fails every required
  // check, so it is blocked too. Drives the disabled state and inline reason.
  const missingSettingsFor = (flow: Flow) => {
    const missing: Array<"requiresAssetAllocation" | "requiresTimeseries"> = [];
    if (requiredFlags.requiresAssetAllocation && !flow.collectionSettings?.requiresAssetAllocation)
      missing.push("requiresAssetAllocation");
    if (requiredFlags.requiresTimeseries && !flow.collectionSettings?.requiresTimeseries)
      missing.push("requiresTimeseries");
    return missing;
  };

  const selectedFlowBlocked = useMemo(() => {
    const flow = filteredFlows.find((f) => f.id === flowId);
    return flow ? missingSettingsFor(flow).length > 0 : false;
  }, [filteredFlows, flowId, requiredFlags]);

  // QL-212 — the section scopes this template places `risk_assessment` under. A
  // template ships against the default (per-goal) scopes, so attaching it to a
  // flow whose model captures every input at client level leaves the block with
  // nothing to print. The server copies a template verbatim rather than refusing
  // the combination, so the warning below is where the designer finds out.
  const riskPlacementScopes = useMemo(() => {
    const scopes: PdfSectionScope[] = [];
    for (const section of selectedTemplate?.sections ?? []) {
      const prints = section.pages.some((page) =>
        page.components.some((comp) => comp.type === "risk_assessment"),
      );
      if (prints) scopes.push(section.scope);
    }
    return scopes;
  }, [selectedTemplate]);

  // True only when the template DOES place the block and NONE of those sections
  // would print it for the chosen flow. Advice, never a gate.
  const riskScopeMismatch = useMemo(() => {
    if (riskPlacementScopes.length === 0) return false;
    const flow = filteredFlows.find((f) => f.id === flowId);
    if (!flow?.riskScopes) return false;
    const scopes = resolveRiskInputScopes(flow.riskScopes);
    return riskPlacementScopes.every(
      (sectionScope) => renderedRiskSectionsInPdfSection(scopes, sectionScope).size === 0,
    );
  }, [riskPlacementScopes, filteredFlows, flowId]);

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      setStep("select");
      setSelectedTemplate(null);
      setSearchQuery("");
      setExpandedCategories(new Set(["basic"]));
      setName("");
      setDescription("");
      setLanguage(supportedLanguages.includes(userLanguage) ? userLanguage : defaultLanguage);
      setFlowId("");
      setError(null);
    }
  }, [open, userLanguage, supportedLanguages, defaultLanguage]);

  // Update name and description when template is selected
  useEffect(() => {
    if (selectedTemplate) {
      setName(selectedTemplate.name);
      setDescription(selectedTemplate.description);
    }
  }, [selectedTemplate]);

  // Reset selected template and flowId when language changes
  useEffect(() => {
    setSelectedTemplate(null);
    const flowsForLanguage = flows.filter((f) => f.language === language);
    setFlowId(flowsForLanguage[0]?.id || "");
  }, [language, flows]);

  // Once a template is chosen, keep the selected flow eligible: if the current
  // flow can't satisfy the template's components, switch to the first one that can.
  useEffect(() => {
    if (!flowId) return;
    const current = filteredFlows.find((f) => f.id === flowId);
    if (current && missingSettingsFor(current).length === 0) return;
    const firstEligible = filteredFlows.find((f) => missingSettingsFor(f).length === 0);
    if (firstEligible && firstEligible.id !== flowId) setFlowId(firstEligible.id);
  }, [requiredFlags, filteredFlows, flowId]);

  const handleNext = () => {
    if (selectedTemplate) {
      setStep("configure");
    }
  };

  const handleBack = () => {
    setStep("select");
    setError(null);
  };

  // Group templates by category, filtered by language (single iteration)
  const templatesByCategory = useMemo(() => {
    const grouped: Record<PdfTemplateCategory, PdfTemplate[]> = {
      basic: [],
      advisory: [],
      execution: [],
      reporting: [],
    };

    const query = searchQuery.toLowerCase();

    for (const template of PDF_TEMPLATES) {
      // Filter by language first
      if (template.language !== language) continue;

      // Then filter by search query
      if (searchQuery) {
        const matchesQuery =
          template.name.toLowerCase().includes(query) ||
          template.description.toLowerCase().includes(query) ||
          template.badges.some((badge) => badge.toLowerCase().includes(query));
        if (!matchesQuery) continue;
      }

      grouped[template.category].push(template);
    }

    return grouped;
  }, [searchQuery, language]);

  // Get sorted categories
  const sortedCategories = useMemo(() => {
    return [...PDF_TEMPLATE_CATEGORIES];
  }, []);

  // Auto-expand all categories when searching
  useEffect(() => {
    if (searchQuery.trim()) {
      setExpandedCategories(
        new Set(sortedCategories.filter((cat) => templatesByCategory[cat].length > 0)),
      );
    }
  }, [searchQuery, sortedCategories, templatesByCategory]);

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories((prev) => {
      if (prev.has(categoryId)) {
        return new Set();
      }
      // Opening this category - also select first template
      const firstInCategory = templatesByCategory[categoryId as PdfTemplateCategory]?.[0];
      if (firstInCategory) {
        setSelectedTemplate(firstInCategory);
      }
      return new Set([categoryId]);
    });
  };

  // Calculate template stats
  const getTemplateStats = (template: PdfTemplate) => {
    const totalPages = template.sections.reduce((acc, s) => acc + s.pages.length, 0);
    const totalComponents = template.sections.reduce(
      (acc, s) => acc + s.pages.reduce((pacc, p) => pacc + p.components.length, 0),
      0,
    );
    return { sections: template.sections.length, pages: totalPages, components: totalComponents };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTemplate) return;

    if (!name.trim()) {
      setError(t.templateNameRequired);
      return;
    }

    if (!flowId) {
      setError(t.pleaseSelectFlow);
      return;
    }

    setError(null);

    try {
      const result = await createFromTemplateMutation.mutateAsync({
        name: name.trim(),
        description: description.trim() || undefined,
        flowId,
        language,
        fontFamily: selectedTemplate.fontFamily,
        fontSize: selectedTemplate.fontSize,
        tableStyleType: selectedTemplate.tableStyleType,
        tableHeaderBgColor: selectedTemplate.tableHeaderBgColor,
        tableHeaderTextColor: selectedTemplate.tableHeaderTextColor,
        tableAlternatingColor: selectedTemplate.tableAlternatingColor,
        sections: selectedTemplate.sections,
      });
      navigate(`/settings/pdf-templates/${result.id}`);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t.failedToCreate);
    }
  };

  // Get category display info
  const getCategoryInfo = (category: PdfTemplateCategory) => t.categories[category];

  return (
    <Dialog open={open} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-4xl h-[600px] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {step === "select"
              ? t.createFromTemplate
              : `${t.configureTemplate}: ${selectedTemplate?.name}`}
          </DialogTitle>
          <DialogDescription>
            {step === "select" ? t.templateDialogDescription : t.configureTemplateDescription}
          </DialogDescription>
        </DialogHeader>

        {step === "select" ? (
          <>
            {/* Language selector at the top */}
            <div className="flex items-center gap-3 py-2">
              <Label htmlFor="language-select" className="text-sm whitespace-nowrap">
                {t.language}:
              </Label>
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger id="language-select" className="w-48">
                  <SelectValue placeholder={t.selectLanguage} />
                </SelectTrigger>
                <SelectContent>
                  {supportedLanguages.map((lang) => (
                    <SelectItem key={lang} value={lang}>
                      {getLocalizedLanguageName(lang, userLanguage)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-[240px_1fr] gap-4 flex-1 overflow-hidden">
              {/* Left: Categories and template list */}
              <div className="border rounded-lg overflow-hidden flex flex-col">
                {/* Search input */}
                <div className="p-2 border-b">
                  <SearchInput
                    data-dialog-guard-ignore
                    placeholder={t.searchTemplates}
                    value={searchQuery}
                    onValueChange={setSearchQuery}
                    inputClassName="h-8 text-sm"
                  />
                </div>

                {/* Template list grouped by collapsible categories */}
                <div className="flex-1 overflow-y-auto">
                  {sortedCategories.every((cat) => templatesByCategory[cat].length === 0) ? (
                    <div className="p-4 text-sm text-muted-foreground text-center">
                      {t.noTemplatesFound}
                    </div>
                  ) : (
                    sortedCategories.map((category) => {
                      const templates = templatesByCategory[category];
                      if (templates.length === 0) return null;

                      const isSearching = searchQuery.trim().length > 0;
                      const isExpanded = isSearching || expandedCategories.has(category);
                      const hasSelectedTemplate = templates.some(
                        (template) => template.key === selectedTemplate?.key,
                      );
                      const categoryInfo = getCategoryInfo(category);

                      return (
                        <div key={category}>
                          {/* Category header */}
                          <button
                            type="button"
                            onClick={() => !isSearching && toggleCategory(category)}
                            className={cn(
                              "w-full flex items-center justify-between px-3 py-2 bg-muted/50 border-b text-sm font-medium transition-colors",
                              isSearching ? "cursor-default" : "hover:bg-muted",
                              hasSelectedTemplate && !isExpanded ? "text-primary" : "",
                            )}
                          >
                            <span className="flex items-center gap-2">
                              {!isSearching &&
                                (isExpanded ? (
                                  <ChevronDown className="h-4 w-4" />
                                ) : (
                                  <ChevronRight className="h-4 w-4" />
                                ))}
                              <span className="truncate">{categoryInfo.name}</span>
                            </span>
                            <span className="text-xs text-muted-foreground">
                              ({templates.length})
                            </span>
                          </button>

                          {/* Templates in category */}
                          {isExpanded &&
                            templates.map((template) => {
                              const stats = getTemplateStats(template);
                              return (
                                <button
                                  key={template.key}
                                  type="button"
                                  onClick={() => setSelectedTemplate(template)}
                                  className={cn(
                                    "w-full text-left pl-9 pr-3 py-2 border-b last:border-b-0 transition-colors",
                                    selectedTemplate?.key === template.key
                                      ? "bg-primary text-primary-foreground"
                                      : "hover:bg-muted",
                                  )}
                                >
                                  <div className="font-medium text-sm truncate">
                                    {template.name}
                                  </div>
                                  <div
                                    className={cn(
                                      "text-xs truncate",
                                      selectedTemplate?.key === template.key
                                        ? "text-primary-foreground/70"
                                        : "text-muted-foreground",
                                    )}
                                  >
                                    {stats.sections} {t.sections.toLowerCase()}, {stats.pages}{" "}
                                    {t.pages.toLowerCase()}
                                  </div>
                                </button>
                              );
                            })}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Right: Template details */}
              <div className="p-4 border rounded-lg bg-muted/30 overflow-y-auto">
                {selectedTemplate ? (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">{selectedTemplate.name}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {selectedTemplate.description}
                    </p>

                    {/* Badges */}
                    <div className="flex flex-wrap gap-1">
                      {selectedTemplate.badges.map((badge) => (
                        <Badge key={badge} variant="outline" className="text-xs">
                          {badge}
                        </Badge>
                      ))}
                    </div>

                    {/* Section summary */}
                    <div className="pt-2 border-t">
                      <p className="text-xs font-medium text-muted-foreground mb-2">
                        {t.sections} ({selectedTemplate.sections.length}):
                      </p>
                      <div className="space-y-1">
                        {selectedTemplate.sections.map((section, idx) => (
                          <div
                            key={section.name}
                            className="flex items-center gap-2 text-xs text-muted-foreground"
                          >
                            <span className="w-4 text-center">{idx + 1}.</span>
                            <span className="flex-1 truncate">{section.name}</span>
                            <Badge variant="outline" className="text-[10px] px-1 py-0">
                              {section.scope === "global"
                                ? t.global
                                : section.scope === "per_portfolio"
                                  ? t.perPortfolio
                                  : t.perGoal}
                            </Badge>
                            <span className="text-[10px]">
                              {section.pages.length} {t.pages.toLowerCase()}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">{t.selectTemplatePrompt}</p>
                )}
              </div>
            </div>

            <DialogFooter>
              <DialogCancel>{t.cancel}</DialogCancel>
              <Button onClick={handleNext} disabled={!selectedTemplate}>
                {t.next}
              </Button>
            </DialogFooter>
          </>
        ) : (
          // Step 2: Configure
          <form onSubmit={handleSubmit} className="flex-1 flex flex-col py-4">
            <div className="flex-1 space-y-6">
              {error && (
                <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label>{t.language}</Label>
                <p className="text-sm text-muted-foreground">
                  {getLocalizedLanguageName(language, userLanguage)}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="template-name">
                  {t.templateName} <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="template-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t.templateNamePlaceholder}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="template-description">{t.description}</Label>
                <Textarea
                  id="template-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t.descriptionPlaceholder}
                  rows={3}
                />
              </div>

              {/* Flow selector - filtered by language */}
              <div className="space-y-2">
                <Label htmlFor="flow-id">
                  {t.flow} <span className="text-destructive">*</span>
                </Label>
                {filteredFlows.length > 0 ? (
                  <Select value={flowId} onValueChange={setFlowId}>
                    <SelectTrigger id="flow-id">
                      <SelectValue placeholder={t.selectFlow} />
                    </SelectTrigger>
                    <SelectContent>
                      {filteredFlows.map((flow) => {
                        const missing = missingSettingsFor(flow);
                        const reason = missing
                          .map((k) =>
                            k === "requiresTimeseries"
                              ? t.missingTimeseries
                              : t.missingAssetAllocation,
                          )
                          .join(", ");
                        return (
                          <SelectItem key={flow.id} value={flow.id} disabled={missing.length > 0}>
                            <span>{flow.name}</span>
                            {missing.length > 0 && (
                              <span className="text-muted-foreground">
                                {" "}
                                — {t.missingPrefix} {reason}
                              </span>
                            )}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="text-sm text-muted-foreground">{t.noFlowsForLanguage}</p>
                )}
                {riskScopeMismatch && <p className="text-xs text-warning">{t.riskScopeMismatch}</p>}
              </div>
            </div>

            <DialogFooter className="pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleBack}
                disabled={createFromTemplateMutation.isPending}
              >
                {t.back}
              </Button>
              <Button
                type="submit"
                loading={createFromTemplateMutation.isPending}
                disabled={!flowId || selectedFlowBlocked}
              >
                {t.create}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
